// ***********************************************************************
// Assembly         : Veriteq.Partner.Web
// Author           : Mike Palmer
// Created          : 06-21-2024
//
// Last Modified By : Mike Palmer
// Last Modified On : 06-21-2024
// ***********************************************************************
// <copyright file="_handler.js" company="NSA Group Ltd">
//     Copyright (c) NSA Group Ltd. All rights reserved.
// </copyright>
// <summary>
//     JavaScript utilities for handling browser events such as the beforeunload
//     event, enabling communication between JavaScript and Blazor components.
// </summary>
// ***********************************************************************

window.handler = window.handlers || {};

/**
 * Sets up a listener for the beforeunload event, ensuring that when the user
 * navigates away from the page, a .NET method is invoked to perform the necessary
 * cleanup, such as unlocking a record in the database.
 *
 * @function setupBeforeUnload
 * @memberof window.handlers
 * @param {object} dotNetObject - A reference to the Blazor .NET object, used to invoke methods in the .NET code.
 */
window.handler.setupBeforeUnload = function (dotNetObject) {
    window.addEventListener('beforeunload', function (_) {
        dotNetObject.invokeMethodAsync('UnlockRecord');
    });
};

/**
 * Checks whether the browser allows popups. This is used to ensure that
 * features requiring popup access (e.g., downloading PDFs or triggering external
 * auth flows) can function correctly.
 *
 * @function checkPopupAllowed
 * @memberof window.handler
 * @returns {boolean} True if popups are allowed, false if blocked.
 */
window.handler.checkPopupAllowed = function () {
    try {
        const popup = window.open("about:blank", "_blank", "width=100,height=100");
        if (!popup || popup.closed || typeof popup.closed === "undefined") {
            return false;
        }
        popup.close();
        return true;
    } catch {
        return false;
    }
}

/**
 * Attempts to open a new browser window (popup) with the specified URL.
 * If the popup is blocked by the browser (e.g., due to user settings),
 * the user is notified with an alert.
 *
 * This method is commonly used for actions like downloading files,
 * printing documents, or starting external authentication flows
 * that require a new window context.
 *
 * @function tryOpenWindow
 * @memberof window.handler
 * @param {string} url - The URL to open in a new popup window.
 * @returns {boolean} Returns true if the popup was successfully opened, false if blocked.
 */
window.handler.tryOpenWindow = function (url) {
    const popup = window.open(url, "_blank");

    if (!popup || popup.closed || typeof popup.closed === "undefined") {
        alert("Popups are blocked! Please enable popups for this site to continue.");
        return false;
    }

    return true;
};

/**
 * Scrolls the specified DOM element into view smoothly. This is typically used
 * to bring a referenced section of a Blazor component into the visible area of the viewport.
 *
 * @function scrollToElementRef
 * @memberof window.handler
 * @param {HTMLElement} element - The DOM element to scroll into view.
 */
window.handler.scrollToElementRef = function (element) {
    if (element) {
        element.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
};

/**
 * Scrolls smoothly to a DOM element by its ID.
 *
 * @function scrollToElementId
 * @memberof window.handler
 * @param {string} elementId - The ID of the DOM element to scroll to.
 */
window.handler.scrollToElementId = function (elementId) {
    const el = document.getElementById(elementId);
    if (el) {
        el.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
};

/**
 * Starts monitoring for user inactivity (idle time). When the user has not
 * interacted (mouse or keyboard) for the specified number of seconds, a .NET
 * method is invoked to allow the Blazor app to perform a session lock or logout.
 * Additionally, a warning can be triggered partway through the timeout window,
 * allowing the user to cancel the pending logout.
 *
 * * This function also registers a handler for browser/tab close (via `beforeunload`)
 *  * to invoke a .NET method and allow logout to be logged correctly.
 *
 * @function startIdleMonitorWithWarning
 * @memberof window.handler
 * @param {object} dotNetObject - A reference to the Blazor .NET object, used to invoke methods in .NET.
 * @param {number} totalTimeoutSeconds - The total idle timeout in seconds (e.g., 900 for 15 minutes).
 * @param {number} warningLeadTimeSeconds - The number of seconds before timeout to trigger a warning (e.g., 300 for 5-minute warning).
 */
window.handler.startIdleMonitorWithWarning = function (dotNetObject, totalTimeoutSeconds, warningLeadTimeSeconds) {
    let idleTime = 0;
    let warned = false;

    function resetIdleTime() {
        idleTime = 0;
        warned = false;
    }

    window.handler._resetIdleTime = resetIdleTime;

    clearInterval(window.handler._idleInterval);
    window.handler._idleInterval = setInterval(() => {
        idleTime++;
        if (idleTime >= totalTimeoutSeconds) {
            clearInterval(window.handler._idleInterval);
            dotNetObject.invokeMethodAsync('OnUserIdle');
        } else if (idleTime >= (totalTimeoutSeconds - warningLeadTimeSeconds) && !warned) {
            warned = true;
            dotNetObject.invokeMethodAsync('OnIdleWarning');
        }
    }, 1000);

    window.addEventListener('mousemove', resetIdleTime);
    window.addEventListener('keydown', resetIdleTime);
    window.addEventListener('beforeunload', () => {
        const sessionId = localStorage.getItem('session_id');
        if (sessionId) {
            navigator.sendBeacon(`/api/user/browser-closed?sessionId=${sessionId}`)
        }
    }, {passive: true});
};

window.handler.resetIdleMonitor = function () {
    if (window.handler._resetIdleTime) {
        window.handler._resetIdleTime();
    }
};

/**
 * Resets the idle timer manually, typically after the user responds to a warning dialog.
 *
 * @function resetIdleMonitor
 * @memberof window.handler
 */
window.handler.resetIdleMonitor = function () {
    window.handler._idleTime = 0;
};

/**
 * Stops the idle monitoring timer and removes event listeners.
 * Should be called during component disposal to clean up resources.
 *
 * @function stopIdleMonitor
 * @memberof window.handler
 */
window.handler.stopIdleMonitor = function () {
    clearInterval(window.handler._idleInterval);
    window.handler.resetIdleMonitor();
    window.removeEventListener('mousemove', () => {
    });
    window.removeEventListener('keydown', () => {
    });
    window.removeEventListener('beforeunload', window.handler._beforeUnloadHandler);
    window.handler._beforeUnloadHandler = null;
};

/**
 * Gets the current browser version.
 * @function stopIdleMonitor
 * @memberof window.handler
 */
window.handler.getBrowser = function () {
    const ua = navigator.userAgent;
    if (ua.includes("Firefox/")) return "Firefox";
    if (ua.includes("Edg/")) return "Edge";
    if (ua.includes("Chrome/") && !ua.includes("Edg/") && !ua.includes("OPR/") && !ua.includes("MiuiBrowser") && !ua.includes("MiBrowser")) return "Chrome";
    if (ua.includes("Safari/") && !ua.includes("Chrome/")) return "Safari";
    return "Unknown";
};